import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16">
          <p className="text-[#DC143C] text-sm uppercase tracking-[0.2em] font-semibold mb-3">
            Get In Touch
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-4">
            Contact Us
          </h1>
          <p className="text-[#9CA3AF] text-base md:text-lg max-w-xl">
            Have a question or want to discuss a deal? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="p-6 md:p-8 bg-[#0A0A12] border border-white/10 rounded-lg space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Name *</label>
                  <Input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white"
                  />
                </div>
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Email *</label>
                  <Input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Phone</label>
                  <Input
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white"
                  />
                </div>
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Subject</label>
                  <Input
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2">Message *</label>
                <Textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-[#05050A] border-white/15 focus:border-[#DC143C] text-white resize-none"
                />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="bg-[#DC143C] text-white hover:bg-[#B01030] font-semibold tracking-wide px-8 py-3"
              >
                {submitting ? "Sending..." : (
                  <span className="flex items-center gap-2">
                    <Send size={16} /> Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="p-6 bg-[#0A0A12] border border-white/10 rounded-lg">
              <h3 className="text-white font-semibold text-lg mb-4">Contact Information</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-[#DC143C]" />
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-widest mb-1">Email</p>
                    <p className="text-white text-sm">info@iet.ae</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center shrink-0">
                    <Phone size={18} className="text-[#DC143C]" />
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-widest mb-1">Phone</p>
                    <p className="text-white text-sm">+971 4 2868441</p>
                    <p className="text-white text-sm">+971 56 56 88227</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center shrink-0">
                    <MapPin size={18} className="text-[#DC143C]" />
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-widest mb-1">Address</p>
                    <p className="text-white text-sm">Office No 202, Al Tawhidi 2,<br />Bur Dubai, P.O. Box No. 242563,<br />Dubai, UAE</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center shrink-0">
                    <Clock size={18} className="text-[#DC143C]" />
                  </div>
                  <div>
                    <p className="text-[#9CA3AF] text-xs uppercase tracking-widest mb-1">Business Hours</p>
                    <p className="text-white text-sm">Mon - Fri: 9:00 AM - 6:00 PM</p>
                    <p className="text-[#9CA3AF] text-xs mt-0.5">Saturday & Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 bg-[#0A0A12] border border-white/10 rounded-lg">
              <h3 className="text-white font-semibold text-lg mb-3">Quick Response</h3>
              <p className="text-[#9CA3AF] text-sm leading-relaxed">
                We aim to respond to all inquiries within 24 hours during business days.
                For urgent matters, please call us directly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
